import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../api/client';
import { toast } from 'sonner';
import { 
  LayoutDashboard, FolderOpen, Plus, Menu, X, LogOut, User,
  Book, Shield, ExternalLink, ChevronDown, Terminal, FileCode
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

interface Project { id: string; name: string; subdomain: string; }

export default function Layout() {
  const { user, logout } = useAuth();
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadProjects(); }, [location.pathname]);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function loadProjects() {
    try {
      const data = await api.getProjects();
      setProjects(data.projects?.slice(0, 15) || []);
    } catch { /* silent */ }
  }

  const isEditorPage = location.pathname.includes('/editor');

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Top Navbar */}
      <nav className="glass border-b border-border h-11 flex items-center px-3 shrink-0 z-30 sticky top-0">
        {/* Left: Logo + Nav Links */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1.5 rounded-lg hover:bg-elevated text-text-muted hover:text-text-primary lg:hidden">
            {mobileOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0 group">
            <img src="/logo.svg" alt="ApexDev" className="w-6 h-6" />
            <span className="font-bold text-base bg-gradient-to-r from-accent to-accent-blue bg-clip-text text-transparent">
              ApexDev
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1 ml-4">
            <Link to="/dashboard" className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${location.pathname === '/dashboard' ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
              Dashboard
            </Link>
            <Link to="/docs" className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${location.pathname === '/docs' ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
              Docs
            </Link>
            <Link to="/terms" className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${location.pathname === '/terms' ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
              Terms
            </Link>
          </div>
        </div>

        {/* Right: Actions + Profile */}
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/new" className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
            <Plus size={14} /> New Project
          </Link>

          {/* External links */}
          <a href="https://apis.megan.qzz.io" target="_blank" rel="noopener noreferrer" 
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-elevated transition-colors hidden md:block" title="Megan APIs">
            <Terminal size={15} />
          </a>

          {/* Profile dropdown */}
          <div className="relative" ref={profileRef}>
            <button onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-elevated transition-colors">
              <div className="w-6 h-6 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-[11px] font-bold text-accent">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </div>
              <ChevronDown size={12} className="text-text-muted hidden sm:block" />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 card p-1 z-50 animate-scale-in">
                <div className="px-3 py-2 border-b border-border mb-1">
                  <p className="text-sm font-semibold truncate">{user?.username}</p>
                  <p className="text-[10px] text-text-muted truncate">{user?.email}</p>
                  <span className="badge badge-success text-[9px] mt-1 capitalize">{user?.tier}</span>
                </div>
                <Link to="/profile" onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:text-text-primary hover:bg-elevated transition-colors">
                  <User size={14} /> Profile
                </Link>
                <Link to="/docs" onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:text-text-primary hover:bg-elevated transition-colors lg:hidden">
                  <Book size={14} /> Docs
                </Link>
                <Link to="/terms" onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:text-text-primary hover:bg-elevated transition-colors lg:hidden">
                  <Shield size={14} /> Terms
                </Link>
                <div className="border-t border-border mt-1 pt-1">
                  <button onClick={() => { setProfileOpen(false); logout(); }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-muted hover:text-danger hover:bg-elevated transition-colors w-full text-left">
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main content area: sidebar + page */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-11 z-20 h-[calc(100vh-2.75rem)] glass border-r border-border flex flex-col shrink-0
          transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${desktopCollapsed ? 'lg:w-14' : 'w-56'}
        `}>
          {/* Mobile close button */}
          <div className="lg:hidden flex items-center justify-end p-2 border-b border-border">
            <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-elevated text-text-muted">
              <X size={16} />
            </button>
          </div>

          <nav className="px-2 py-3 space-y-0.5 shrink-0">
            <Link to="/dashboard" onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/dashboard' ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
              <LayoutDashboard size={18} />
              {!desktopCollapsed && 'Dashboard'}
            </Link>
            <Link to="/new" onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/new' ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
              <Plus size={18} />
              {!desktopCollapsed && 'New Project'}
            </Link>
          </nav>

          {/* Projects list */}
          {!desktopCollapsed && (
            <div className="flex-1 overflow-y-auto px-2 py-2">
              <p className="px-3 py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1">Projects</p>
              {projects.map(p => (
                <Link key={p.id} to={`/project/${p.id}`} onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors truncate ${location.pathname.includes(p.id) ? 'bg-elevated text-accent' : 'text-text-muted hover:text-text-primary hover:bg-elevated'}`}>
                  <FolderOpen size={14} className="shrink-0" />
                  <span className="truncate">{p.name}</span>
                </Link>
              ))}
            </div>
          )}
          {desktopCollapsed && <div className="flex-1" />}

          {/* Sidebar toggle */}
          <button onClick={() => setDesktopCollapsed(!desktopCollapsed)}
            className="hidden lg:flex items-center justify-center p-2 border-t border-border text-text-muted hover:text-text-primary transition-colors">
            {desktopCollapsed ? <Menu size={14} /> : <X size={14} />}
          </button>

          {/* User footer in sidebar (desktop collapsed) */}
          {desktopCollapsed && (
            <div className="border-t border-border p-2 flex justify-center">
              <div className="w-7 h-7 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-xs font-bold text-accent">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </div>
            </div>
          )}
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 bg-black/60 z-10 lg:hidden" onClick={() => setMobileOpen(false)} />
        )}

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
